import DensityLargeIcon from "@mui/icons-material/DensityLarge";
import DensityMediumIcon from "@mui/icons-material/DensityMedium";
import DensitySmallIcon from "@mui/icons-material/DensitySmall";
import FilterListIcon from "@mui/icons-material/FilterList";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  FormControlLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Popper,
  Switch,
  TextField,
} from "@mui/material";
import type { RowData } from "@tanstack/react-table";
import React, { useState } from "react";
import FilterPanel, { newFilterItem, nextFilterId } from "./FilterPanel";
import type { DataTableInstance } from "./features";
import type { DataTableColDef, Density, FilterModel } from "./types";

export type ToolbarPanel = "filters" | "columns";

const DENSITY_OPTIONS: Array<{
  value: Density;
  label: string;
  icon: React.ReactElement;
}> = [
  { value: "compact", label: "Compact", icon: <DensitySmallIcon /> },
  { value: "standard", label: "Standard", icon: <DensityMediumIcon /> },
  { value: "comfortable", label: "Comfortable", icon: <DensityLargeIcon /> },
];

// Clicks inside MUI popups land outside the panel's DOM and must not close it.
function isInsidePopup(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    Boolean(target.closest(".MuiPopover-root, .MuiAutocomplete-popper"))
  );
}

interface ColumnsPanelProps<R extends RowData> {
  table: DataTableInstance<R>;
  columns: DataTableColDef<R>[];
  onResetLayout: () => void;
}

function ColumnsPanel<R extends RowData>({
  table,
  columns,
  onResetLayout,
}: ColumnsPanelProps<R>) {
  const [search, setSearch] = useState("");
  const labels = new Map(
    columns.map((column) => [column.field, column.headerName ?? column.field]),
  );
  const hideable = table
    .getAllLeafColumns()
    .filter((column) => column.getCanHide());
  const shown = hideable.filter((column) =>
    (labels.get(column.id) ?? column.id)
      .toLowerCase()
      .includes(search.toLowerCase()),
  );
  const setAll = (visible: boolean) =>
    table.setColumnVisibility((current) => ({
      ...current,
      ...Object.fromEntries(hideable.map((column) => [column.id, visible])),
    }));

  return (
    <Box sx={{ padding: "8px 16px", width: 300 }}>
      <TextField
        variant="standard"
        label="Find column"
        placeholder="Column title"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        fullWidth
        autoFocus
      />
      <Box sx={{ maxHeight: 360, overflowY: "auto", py: 1 }}>
        {shown.map((column) => (
          <FormControlLabel
            key={column.id}
            sx={{ display: "flex", ml: 0 }}
            control={
              <Switch
                size="small"
                checked={column.getIsVisible()}
                onChange={(event) =>
                  column.toggleVisibility(event.target.checked)
                }
              />
            }
            label={labels.get(column.id) ?? column.id}
          />
        ))}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Button size="small" onClick={() => setAll(false)}>
          Hide all
        </Button>
        <Button size="small" onClick={() => setAll(true)}>
          Show all
        </Button>
      </Box>
      <Button
        size="small"
        color="inherit"
        onClick={onResetLayout}
        sx={{ mt: 0.5 }}
      >
        Reset layout
      </Button>
    </Box>
  );
}

interface DataTableToolbarProps<R extends RowData> {
  columns: DataTableColDef<R>[];
  table: DataTableInstance<R>;
  openPanel: ToolbarPanel | null;
  onOpenPanel: (panel: ToolbarPanel | null) => void;
  filterModel: FilterModel;
  onFilterModelChange: (model: FilterModel) => void;
  filterFocusField?: string;
  activeFilterCount: number;
  density: Density;
  onDensityChange: (density: Density) => void;
  onExportCsv: () => void;
  exporting: boolean;
  onPrint: () => void;
  onResetLayout: () => void;
  actions?: React.ReactNode;
}

export default function DataTableToolbar<R extends RowData>({
  columns,
  table,
  openPanel,
  onOpenPanel,
  filterModel,
  onFilterModelChange,
  filterFocusField,
  activeFilterCount,
  density,
  onDensityChange,
  onExportCsv,
  exporting,
  onPrint,
  onResetLayout,
  actions,
}: DataTableToolbarProps<R>) {
  const [anchor, setAnchor] = useState<HTMLDivElement | null>(null);
  const [densityAnchor, setDensityAnchor] = useState<HTMLElement | null>(null);
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: seed a row only when the panel opens or is re-targeted
  React.useEffect(() => {
    if (openPanel !== "filters") return;
    const filterable = columns.filter((column) => column.filterable !== false);
    const target =
      filterable.find((column) => column.field === filterFocusField) ??
      filterable[0];
    if (!target) return;
    const hasTarget = filterModel.items.some(
      (item) => item.field === target.field,
    );
    if (filterModel.items.length === 0 || (filterFocusField && !hasTarget)) {
      onFilterModelChange({
        ...filterModel,
        items: [
          ...filterModel.items,
          newFilterItem(target, nextFilterId(filterModel)),
        ],
      });
    }
  }, [openPanel, filterFocusField]);

  const close = (event: MouseEvent | TouchEvent) => {
    if (isInsidePopup(event.target)) return;
    if (
      anchor &&
      event.target instanceof Node &&
      anchor.contains(event.target)
    ) {
      return;
    }
    onOpenPanel(null);
  };

  const toggle = (panel: ToolbarPanel) =>
    onOpenPanel(openPanel === panel ? null : panel);

  const densityIcon =
    DENSITY_OPTIONS.find((option) => option.value === density)?.icon ??
    DENSITY_OPTIONS[1].icon;

  return (
    <Box
      ref={setAnchor}
      className="DataTable-toolbar"
      sx={{
        display: "flex",
        gap: 1,
        padding: "14px 4px 4px",
        flexWrap: "wrap",
      }}
    >
      {actions}
      <Button
        size="small"
        startIcon={<ViewColumnIcon />}
        aria-label="Select columns"
        onClick={() => toggle("columns")}
      >
        Columns
      </Button>
      <Button
        size="small"
        startIcon={
          <Badge badgeContent={activeFilterCount} color="primary">
            <FilterListIcon />
          </Badge>
        }
        aria-label="Show filters"
        onClick={() => toggle("filters")}
      >
        Filters
      </Button>
      <Button
        size="small"
        startIcon={densityIcon}
        aria-label="Density"
        onClick={(event) => setDensityAnchor(event.currentTarget)}
      >
        Density
      </Button>
      <Button
        size="small"
        startIcon={exporting ? <CircularProgress size={16} /> : <SaveAltIcon />}
        aria-label="Export"
        disabled={exporting}
        onClick={(event) => setExportAnchor(event.currentTarget)}
      >
        Export
      </Button>

      <Menu
        anchorEl={densityAnchor}
        open={Boolean(densityAnchor)}
        onClose={() => setDensityAnchor(null)}
      >
        {DENSITY_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === density}
            onClick={() => {
              onDensityChange(option.value);
              setDensityAnchor(null);
            }}
          >
            <ListItemIcon>{option.icon}</ListItemIcon>
            <ListItemText>{option.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
      <Menu
        anchorEl={exportAnchor}
        open={Boolean(exportAnchor)}
        onClose={() => setExportAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            onExportCsv();
            setExportAnchor(null);
          }}
        >
          Download as CSV
        </MenuItem>
        <MenuItem
          onClick={() => {
            onPrint();
            setExportAnchor(null);
          }}
        >
          Print
        </MenuItem>
      </Menu>

      <Popper
        open={Boolean(openPanel && anchor)}
        anchorEl={anchor}
        placement="bottom-start"
        sx={{ zIndex: 1300 }}
      >
        {/* Mousedown: a select's backdrop sits under the mouseup, so click reads as outside. */}
        <ClickAwayListener
          onClickAway={close}
          mouseEvent="onMouseDown"
          touchEvent="onTouchStart"
        >
          <Paper elevation={8}>
            {openPanel === "filters" && (
              <FilterPanel
                columns={columns}
                filterModel={filterModel}
                onFilterModelChange={onFilterModelChange}
              />
            )}
            {openPanel === "columns" && (
              <ColumnsPanel
                table={table}
                columns={columns}
                onResetLayout={onResetLayout}
              />
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}
